// src/components/ProductCard.jsx
import React, { useState } from 'react';
import { useCart } from '../context/CartContext';

const ProductCard = ({ product }) => {
  const [selectedOption, setSelectedOption] = useState(product.priceOptions?.[0] || null);
  const [isAdding, setIsAdding] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { addToCart } = useCart();

  const handleAddToCart = async () => {
    setIsAdding(true);
    
    // Simulate loading
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (product.priceOptions) {
      addToCart(product, selectedOption);
    } else {
      addToCart(product);
    }
    
    setIsAdding(false);
  };

  const getStrainColor = (strain) => {
    switch(strain?.toLowerCase()) {
      case 'sativa': return { 
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
        color: '#92400e',
        emoji: '☀️'
      };
      case 'indica': return { 
        background: 'linear-gradient(135deg, #e9d5ff 0%, #d8b4fe 100%)', 
        color: '#6b21a8',
        emoji: '🌙'
      };
      case 'hybrid': return { 
        background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', 
        color: '#065f46',
        emoji: '⚡'
      };
      default: return { 
        background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)', 
        color: '#374151',
        emoji: '🌿'
      };
    }
  };

  const strainStyle = getStrainColor(product.strain);

  // Format category name
  const formatCategory = (category) => {
    return category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get placeholder emoji based on category
  const getPlaceholderEmoji = () => {
    const category = product.category?.toLowerCase() || '';
    if (category.includes('flower')) return '🌿';
    if (category.includes('edible')) return '🍪';
    if (category.includes('vape')) return '💨';
    if (category.includes('concentrate')) return '🔥';
    if (category.includes('tincture')) return '💧';
    if (category.includes('topical')) return '🧴';
    if (category.includes('accessory')) return '🔧';
    return '🌿';
  };

  return (
    <div 
      className="product-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--card)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: isHovered ? 'var(--shadow-2xl)' : 'var(--shadow-lg)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        position: 'relative',
        cursor: 'pointer'
      }}
      onClick={() => {/* Optional: Navigate to product detail */}}
    >
      {/* Image Container */}
      <div style={{
        position: 'relative',
        paddingTop: '75%', // 4:3 Aspect Ratio
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        flexShrink: 0
      }}>
        {/* Image or Placeholder */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'clamp(3rem, 10vw, 5rem)',
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          transition: 'transform 0.3s ease'
        }}>
          {product.image && !imageError ? (
            <img 
              src={product.image} 
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
              loading="lazy"
              onError={() => setImageError(true)}
            />
          ) : (
            getPlaceholderEmoji()
          )}
        </div>

        {/* Badges Container */}
        <div style={{
          position: 'absolute',
          top: 'clamp(8px, 2vw, 12px)',
          left: 'clamp(8px, 2vw, 12px)',
          right: 'clamp(8px, 2vw, 12px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          zIndex: 2,
          pointerEvents: 'none',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          {/* Left Badges */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(4px, 1vw, 8px)',
            alignItems: 'flex-start'
          }}>
            {product.isNew && (
              <span style={{
                padding: 'clamp(4px, 1vw, 6px) clamp(8px, 2vw, 12px)',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                borderRadius: '20px',
                fontSize: 'clamp(0.6rem, 1.5vw, 0.75rem)',
                fontWeight: '700',
                lineHeight: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
              }}>
                🔥 New
              </span>
            )}
            
            {product.strain && (
              <span style={{
                padding: 'clamp(4px, 1vw, 6px) clamp(8px, 2vw, 12px)',
                background: strainStyle.background,
                color: strainStyle.color,
                borderRadius: '20px',
                fontSize: 'clamp(0.6rem, 1.5vw, 0.75rem)',
                fontWeight: '700',
                lineHeight: 1,
                letterSpacing: '0.05em',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span>{strainStyle.emoji}</span>
                <span>{product.strain}</span>
              </span>
            )}
          </div>

          {/* Right Badges */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(4px, 1vw, 8px)',
            alignItems: 'flex-end'
          }}>
            {product.popular && (
              <span style={{
                padding: 'clamp(4px, 1vw, 6px) clamp(8px, 2vw, 12px)',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                borderRadius: '20px',
                fontSize: 'clamp(0.6rem, 1.5vw, 0.75rem)',
                fontWeight: '700',
                lineHeight: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
              }}>
                ⭐ Popular
              </span>
            )}
            
            {product.discount && (
              <span style={{
                padding: 'clamp(4px, 1vw, 6px) clamp(8px, 2vw, 12px)',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                borderRadius: '20px',
                fontSize: 'clamp(0.6rem, 1.5vw, 0.75rem)',
                fontWeight: '700',
                lineHeight: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
              }}>
                -{product.discount}%
              </span>
            )}
          </div>
        </div>

        {/* Quick View Overlay */}
        {isHovered && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
            padding: '20px 12px 12px',
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease',
            zIndex: 3
          }}>
            <button
              style={{
                width: '100%',
                padding: '8px',
                background: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontWeight: '600',
                fontSize: '0.875rem',
                color: 'var(--primary)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Handle quick view
              }}
            >
              Quick View
            </button>
          </div>
        )}
      </div>

      {/* Content Container */}
      <div style={{ 
        padding: 'clamp(12px, 3vw, 20px)',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 'clamp(8px, 2vw, 12px)'
      }}>
        {/* Category & Rating */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <span style={{
            fontSize: 'clamp(0.65rem, 1.5vw, 0.75rem)',
            color: 'var(--primary)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            background: 'rgba(5, 150, 105, 0.1)',
            padding: '4px 8px',
            borderRadius: '4px'
          }}>
            {formatCategory(product.category)}
          </span>

          {product.rating && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              background: 'rgba(0,0,0,0.05)',
              padding: '2px 6px',
              borderRadius: '4px'
            }}>
              <span style={{ color: '#fbbf24', fontSize: '0.875rem' }}>★</span>
              <span style={{ 
                fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)',
                fontWeight: '600',
                color: 'var(--text)'
              }}>
                {product.rating}
              </span>
            </div>
          )}
        </div>

        {/* Name */}
        <h3 style={{
          fontSize: 'clamp(1rem, 3vw, 1.25rem)',
          fontWeight: '700',
          lineHeight: 1.3,
          color: 'var(--text)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '2.6em',
          margin: 0
        }}>
          {product.name}
        </h3>

        {/* Description */}
        <p style={{
          fontSize: 'clamp(0.8rem, 2vw, 0.875rem)',
          color: 'var(--text-light)',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          margin: 0
        }}>
          {product.description}
        </p>

        {/* Key Info Chips */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'clamp(4px, 1vw, 8px)',
          marginTop: '4px'
        }}>
          {product.thcContent && (
            <span style={{
              padding: '4px 8px',
              background: 'rgba(5, 150, 105, 0.1)',
              borderRadius: '4px',
              fontSize: 'clamp(0.7rem, 1.5vw, 0.75rem)',
              fontWeight: '500',
              color: 'var(--primary-dark)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontSize: '0.8rem' }}>⚡</span>
              THC: {product.thcContent}mg
            </span>
          )}
          
          {product.weight && (
            <span style={{
              padding: '4px 8px',
              background: 'rgba(107, 114, 128, 0.1)',
              borderRadius: '4px',
              fontSize: 'clamp(0.7rem, 1.5vw, 0.75rem)',
              fontWeight: '500',
              color: 'var(--text-light)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontSize: '0.8rem' }}>⚖️</span>
              {product.weight}
            </span>
          )}
        </div>

        {/* Price Options */}
        {product.priceOptions && product.priceOptions.length > 0 && (
          <div style={{ 
            marginTop: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(product.priceOptions.length, 3)}, 1fr)`,
              gap: 'clamp(4px, 1vw, 8px)'
            }}>
              {product.priceOptions.slice(0, 3).map((opt) => (
                <button
                  key={opt.option}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOption(opt);
                  }}
                  style={{
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(2px, 1vw, 4px)',
                    fontSize: 'clamp(0.65rem, 1.5vw, 0.75rem)',
                    borderRadius: '6px',
                    border: selectedOption?.option === opt.option ? 
                      '2px solid var(--primary)' : '1px solid var(--border)',
                    background: selectedOption?.option === opt.option ? 
                      'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)' : 'var(--background)',
                    color: selectedOption?.option === opt.option ? 'white' : 'var(--text)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: '600',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1.2,
                    width: '100%'
                  }}
                >
                  <span>{opt.option}</span>
                  <span style={{ 
                    fontSize: '0.6rem',
                    opacity: 0.8,
                    marginTop: '2px'
                  }}>
                    ${opt.price}
                  </span>
                </button>
              ))}
            </div>
            
            {product.priceOptions.length > 3 && (
              <span style={{
                fontSize: '0.7rem',
                color: 'var(--text-light)',
                textAlign: 'center'
              }}>
                +{product.priceOptions.length - 3} more options
              </span>
            )}
          </div>
        )}

        {/* Price and Add to Cart */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'clamp(8px, 2vw, 12px)',
          marginTop: 'auto',
          paddingTop: '8px',
          borderTop: '1px solid var(--border)'
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 'clamp(1.25rem, 4vw, 1.5rem)',
              fontWeight: 'bold',
              color: 'var(--primary)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              ${product.priceOptions ? selectedOption?.price : product.price}
            </div>
            {product.priceOptions && selectedOption && (
              <div style={{
                fontSize: 'clamp(0.65rem, 1.5vw, 0.7rem)',
                color: 'var(--text-light)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {selectedOption.option}
              </div>
            )}
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart();
            }}
            disabled={isAdding}
            style={{
              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 16px)',
              fontSize: 'clamp(0.8rem, 2vw, 0.875rem)',
              fontWeight: '600',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: isAdding ? 'var(--primary-dark)' : 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
              color: 'white',
              cursor: isAdding ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              minWidth: 'clamp(90px, 20vw, 100px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              opacity: isAdding ? 0.7 : 1,
              boxShadow: 'var(--shadow-md)'
            }}
          >
            {isAdding ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span>Adding</span>
              </>
            ) : (
              <>
                <span>🛒</span>
                <span>Add</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Global Styles */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .product-card {
          position: relative;
        }

        /* Mobile Optimization */
        @media (max-width: 480px) {
          .product-card > div:first-child {
            padding-top: 70%; /* Slightly shorter on mobile */
          }
        }

        /* Touch Device Optimization */
        @media (hover: none) {
          .product-card:hover {
            transform: none;
          }
          
          .product-card > div:first-child > div:last-child {
            display: none; /* Hide quick view on touch devices */
          }
        }

        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .product-card {
            background: #2d2d2d;
          }
          
          .product-card h3 {
            color: #ffffff;
          }
          
          .product-card p {
            color: #a0a0a0;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCard;